import { Component, ElementRef, ViewChild } from "@angular/core";
import { DialogBaseComponent, DialogParams } from "./dialog-base.component";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { DomainService } from "./domain.service";
import { UtilFns } from "./util-fns";

export interface EmailPromptDialogParams extends DialogParams {
  title?: string;
  message?: string;
  emailAddress?: string;
}

@Component({
  template: `
    <app-uni-dialog [parent]="this" [contentTemplate]="contentTemplate" [footerTemplate]="footerTemplate"></app-uni-dialog>

    <ng-template #contentTemplate>
      <div class="flex flex-col gap-3">
        <div *ngIf="message" class="text-base">{{ message }}</div>
        <div class="grid grid-cols-12 gap-3">
          <qb-field label="Email Address" class="col-span-12">
            <input
              #emailInput
              pInputText
              [(ngModel)]="emailAddress"
              class="w-full"
              (keydown.enter)="submit()"
              title="Email Address"
            />
          </qb-field>
        </div>
        <div *ngIf="errorMessage" class="text-red-500 text-sm">{{ errorMessage }}</div>
      </div>
    </ng-template>

    <ng-template #footerTemplate>
      <div class="flex flex-row gap-2">
        <button pButton type="button" class="p-button-raised" icon="pi pi-check" (click)="submit()">
          <span>Send</span>
        </button>
        <button pButton type="button" class="p-button-raised p-button-secondary" icon="pi pi-times" (click)="cancel()">
          <span>Cancel</span>
        </button>
      </div>
    </ng-template>
  `,
  standalone: false,
})
export class EmailPromptDialogComponent extends DialogBaseComponent<EmailPromptDialogParams> {
  @ViewChild("emailInput") emailInput: ElementRef<HTMLInputElement>;

  message: string;
  emailAddress: string;
  errorMessage: string;

  constructor(
    public override config: DynamicDialogConfig<EmailPromptDialogParams>,
    public override dialogRef: DynamicDialogRef,
    public override domainService: DomainService
  ) {
    super(config, dialogRef, domainService);
    this.prepare();
  }

  async prepare() {
    this.config.header = this.params.title || "Enter Email Address";
    this.message = this.params.message || "";
    this.emailAddress = this.params.emailAddress || "";
    this.errorMessage = "";
    this.isPageReady = true;

    // Focus the input after render
    await UtilFns.wait(1);
    this.emailInput?.nativeElement?.focus();
  }

  async submit() {
    this.errorMessage = "";

    if (!this.emailAddress || !this.emailAddress.trim()) {
      this.errorMessage = "Please enter an email address.";
      return;
    }

    const email = this.emailAddress.trim();

    // Validate email format
    if (!this.isValidEmail(email)) {
      this.errorMessage = "Please enter a valid email address.";
      return;
    }

    this.dialogRef.close(email);
  }

  cancel() {
    this.dialogRef.close(null);
  }

  private isValidEmail(email: string): boolean {
    // RFC 5322 compliant email regex (simplified but robust)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}
